import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAction, useFindOne, useFindMany, useUser } from '@gadgetinc/react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Textarea,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Progress,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Flex,
  ButtonGroup,
  Spinner,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText
} from '@chakra-ui/react';
import { MdFlashOn, MdArrowBack, MdAssessment, MdHistory, MdSave, MdInfo } from 'react-icons/md';
import { api } from '../api';
import InteractivePatientAssessment from '../components/InteractivePatientAssessment';

const AUTO_SAVE_INTERVAL = 15000; // 15 seconds for database auto-save
const DEBOUNCE_DELAY = 3000; // 3 seconds debounce

const QUICK_START_TEMPLATES = {
  chestPain: {
    name: 'Chest Pain',
    icon: '💓',
    data: {
      chiefComplaint: 'Chest pain',
      patientCriticality: 'red_critical',
      performedExam: true,
      performedInterview: true
    }
  },
  trauma: {
    name: 'Trauma',
    icon: '🚑',
    data: {
      chiefComplaint: 'Traumatic injury',
      patientCriticality: 'red_critical',
      performedExam: true,
      mechanismOfInjury: ['Motor vehicle collision']
    }
  },
  respiratory: {
    name: 'Respiratory',
    icon: '🫁',
    data: {
      chiefComplaint: 'Difficulty breathing',
      patientCriticality: 'yellow_not_life_threatening',
      performedExam: true
    }
  },
  cardiac: {
    name: 'Cardiac Arrest',
    icon: '⚡',
    data: {
      chiefComplaint: 'Cardiac arrest',
      patientCriticality: 'red_critical',
      performedExam: true,
      airwayManagementRequired: true
    }
  },
  general: {
    name: 'General Medical',
    icon: '🏥',
    data: {
      chiefComplaint: '',
      patientCriticality: 'green_ambulatory',
      performedExam: true,
      performedInterview: true
    }
  }
};

export default function PatientEncounters() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useUser(api);
  const { isOpen: isTemplateModalOpen, onOpen: onTemplateModalOpen, onClose: onTemplateModalClose } = useDisclosure();
  const { isOpen: isAssessmentModalOpen, onOpen: onAssessmentModalOpen, onClose: onAssessmentModalClose } = useDisclosure();
  
  // Extract parameters from URL
  const encounterId = searchParams.get('encounterId');
  const shiftId = searchParams.get('shiftId');
  
  // Load existing encounter if ID provided
  const [{ data: existingEncounter, fetching: loadingEncounter, error: loadError }, refetchEncounter] = 
    useFindOne(api.patientEncounter, encounterId, {
      skip: !encounterId,
      select: {
        id: true,
        encounterDate: true,
        cohort: true,
        institution: true,
        student: { id: true, firstName: true, lastName: true },
        preceptor: { id: true, firstName: true, lastName: true },
        shift: { id: true, shiftDate: true, location: true },
        patientAge: true,
        patientAgeMonths: true,
        patientGender: true,
        patientEthnicity: true,
        patientBiologicalSex: true,
        patientPronouns: true,
        chiefComplaint: { markdown: true },
        historyPresentIllness: { markdown: true },
        eventsPrior: { markdown: true },
        lastOralIntake: { markdown: true },
        pastMedicalHistory: { markdown: true },
        medications: { markdown: true },
        allergies: { markdown: true },
        onsetTiming: { markdown: true },
        provocationPalliation: { markdown: true },
        quality: { markdown: true },
        radiation: { markdown: true },
        severity: { markdown: true },
        timing: { markdown: true },
        heartRate: true,
        heartRateUta: true,
        systolicBP: true,
        systolicBPUta: true,
        diastolicBP: true,
        diastolicBPUta: true,
        respiratoryRate: true,
        respiratoryRateUta: true,
        oxygenSaturation: true,
        oxygenSaturationUta: true,
        temperature: true,
        temperatureUta: true,
        bgl: true,
        bglUta: true,
        etco2: true,
        etco2Uta: true,
        patientAlertLevel: true,
        mentalStatus: { markdown: true },
        heentAssessment: { markdown: true },
        neckBackAssessment: { markdown: true },
        cardiacAssessment: { markdown: true },
        heartSounds: { markdown: true },
        chestAssessment: { markdown: true },
        lungSounds: { markdown: true },
        abdomenAssessment: { markdown: true },
        extremitiesAssessment: { markdown: true },
        skinAssessment: { markdown: true },
        neurologicalAssessment: { markdown: true },
        performedExam: true,
        performedInterview: true,
        primaryImpression: true,
        secondaryImpression: true,
        fieldImpressions: { markdown: true },
        differentialDiagnosis: { markdown: true },
        treatmentInterventions: { markdown: true },
        reassessmentResponse: { markdown: true },
        transportDisposition: { markdown: true },
        mechanismOfInjury: true,
        causeOfInjury: true,
        intentOfInjury: true,
        arrestWitnessedBy: true,
        returnOfPulse: true,
        airwayManagementRequired: true,
        airwayManagementSuccess: true,
        patientCriticality: true,
        qaDocumentationCriteria: { markdown: true },
        postIncidentReflection: { markdown: true },
        reviewStatus: true,
        createdAt: true,
        updatedAt: true
      }
    });

  // Check for existing encounters on this shift to prevent duplicates
  const [{ data: existingShiftEncounters }] = useFindMany(api.patientEncounter, {
    filter: { shift: { equals: shiftId } },
    select: { id: true, student: { id: true, firstName: true, lastName: true } },
    skip: !shiftId || !!encounterId // Skip if no shiftId or if we're editing existing
  });
  
  const [saveStatus, setSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle, saving, saved, error
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle, submitting, success, draft_saved, error
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [draftId, setDraftId] = useState(encounterId || null);
  const [currentTab, setCurrentTab] = useState(0);
  
  const STORAGE_KEY = `patient-encounter-draft-${shiftId || 'new'}`;
  
  const autoSaveTimer = useRef(null);
  const debounceTimer = useRef(null);
  const isUnmounting = useRef(false);
  
  const [{ data: createData, fetching: createFetching, error: createError }, createEncounter] = useAction(api.patientEncounter.create);
  const [{ data: updateData, fetching: updateFetching, error: updateError }, updateEncounter] = useAction(api.patientEncounter.update);
  
  // Initialize form data based on existing encounter or defaults
  const [formData, setFormData] = useState(() => {
    const defaultData = {
      patientAge: '',
      patientAgeMonths: '',
      patientGender: '',
      patientEthnicity: '',
      patientBiologicalSex: '',
      patientPronouns: '',
      chiefComplaint: '',
      historyPresentIllness: '',
      eventsPrior: '',
      lastOralIntake: '',
      pastMedicalHistory: '',
      medications: '',
      allergies: '',
      onsetTiming: '',
      provocationPalliation: '',
      quality: '',
      radiation: '',
      severity: '',
      timing: '',
      heartRate: '',
      heartRateUta: false,
      systolicBP: '',
      systolicBPUta: false,
      diastolicBP: '',
      diastolicBPUta: false,
      respiratoryRate: '',
      respiratoryRateUta: false,
      oxygenSaturation: '',
      oxygenSaturationUta: false,
      temperature: '',
      temperatureUta: false,
      bgl: '',
      bglUta: false,
      etco2: '',
      etco2Uta: false,
      patientAlertLevel: '',
      mentalStatus: '',
      heentAssessment: '',
      neckBackAssessment: '',
      cardiacAssessment: '',
      heartSounds: '',
      chestAssessment: '',
      lungSounds: '',
      abdomenAssessment: '',
      extremitiesAssessment: '',
      skinAssessment: '',
      neurologicalAssessment: '',
      performedExam: false,
      performedInterview: false,
      primaryImpression: '',
      secondaryImpression: '',
      fieldImpressions: '',
      differentialDiagnosis: '',
      treatmentInterventions: '',
      reassessmentResponse: '',
      transportDisposition: '',
      mechanismOfInjury: [],
      causeOfInjury: '',
      intentOfInjury: '',
      arrestWitnessedBy: '',
      returnOfPulse: '',
      airwayManagementRequired: false,
      airwayManagementSuccess: '',
      patientCriticality: '',
      qaDocumentationCriteria: '',
      postIncidentReflection: '',
      shift: shiftId || '',
      student: user?.user?.id || '',
      preceptor: '',
      cohort: '',
      institution: '',
      encounterDate: new Date().toISOString().slice(0, 16),
      reviewStatus: 'draft'
    };
    
    return defaultData;
  });

  // Load existing encounter data when it becomes available
  useEffect(() => {
    if (existingEncounter) {
      const encData = existingEncounter;
      setFormData({
        patientAge: encData.patientAge || '',
        patientAgeMonths: encData.patientAgeMonths || '',
        patientGender: encData.patientGender || '',
        patientEthnicity: encData.patientEthnicity || '',
        patientBiologicalSex: encData.patientBiologicalSex || '',
        patientPronouns: encData.patientPronouns || '',
        chiefComplaint: encData.chiefComplaint?.markdown || '',
        historyPresentIllness: encData.historyPresentIllness?.markdown || '',
        eventsPrior: encData.eventsPrior?.markdown || '',
        lastOralIntake: encData.lastOralIntake?.markdown || '',
        pastMedicalHistory: encData.pastMedicalHistory?.markdown || '',
        medications: encData.medications?.markdown || '',
        allergies: encData.allergies?.markdown || '',
        onsetTiming: encData.onsetTiming?.markdown || '',
        provocationPalliation: encData.provocationPalliation?.markdown || '',
        quality: encData.quality?.markdown || '',
        radiation: encData.radiation?.markdown || '',
        severity: encData.severity?.markdown || '',
        timing: encData.timing?.markdown || '',
        heartRate: encData.heartRate || '',
        heartRateUta: encData.heartRateUta || false,
        systolicBP: encData.systolicBP || '',
        systolicBPUta: encData.systolicBPUta || false,
        diastolicBP: encData.diastolicBP || '',
        diastolicBPUta: encData.diastolicBPUta || false,
        respiratoryRate: encData.respiratoryRate || '',
        respiratoryRateUta: encData.respiratoryRateUta || false,
        oxygenSaturation: encData.oxygenSaturation || '',
        oxygenSaturationUta: encData.oxygenSaturationUta || false,
        temperature: encData.temperature || '',
        temperatureUta: encData.temperatureUta || false,
        bgl: encData.bgl || '',
        bglUta: encData.bglUta || false,
        etco2: encData.etco2 || '',
        etco2Uta: encData.etco2Uta || false,
        patientAlertLevel: encData.patientAlertLevel || '',
        mentalStatus: encData.mentalStatus?.markdown || '',
        heentAssessment: encData.heentAssessment?.markdown || '',
        neckBackAssessment: encData.neckBackAssessment?.markdown || '',
        cardiacAssessment: encData.cardiacAssessment?.markdown || '',
        heartSounds: encData.heartSounds?.markdown || '',
        chestAssessment: encData.chestAssessment?.markdown || '',
        lungSounds: encData.lungSounds?.markdown || '',
        abdomenAssessment: encData.abdomenAssessment?.markdown || '',
        extremitiesAssessment: encData.extremitiesAssessment?.markdown || '',
        skinAssessment: encData.skinAssessment?.markdown || '',
        neurologicalAssessment: encData.neurologicalAssessment?.markdown || '',
        performedExam: encData.performedExam || false,
        performedInterview: encData.performedInterview || false,
        primaryImpression: encData.primaryImpression || '',
        secondaryImpression: encData.secondaryImpression || '',
        fieldImpressions: encData.fieldImpressions?.markdown || '',
        differentialDiagnosis: encData.differentialDiagnosis?.markdown || '',
        treatmentInterventions: encData.treatmentInterventions?.markdown || '',
        reassessmentResponse: encData.reassessmentResponse?.markdown || '',
        transportDisposition: encData.transportDisposition?.markdown || '',
        mechanismOfInjury: Array.isArray(encData.mechanismOfInjury) ? encData.mechanismOfInjury : [],
        causeOfInjury: encData.causeOfInjury || '',
        intentOfInjury: encData.intentOfInjury || '',
        arrestWitnessedBy: encData.arrestWitnessedBy || '',
        returnOfPulse: encData.returnOfPulse || '',
        airwayManagementRequired: encData.airwayManagementRequired || false,
        airwayManagementSuccess: encData.airwayManagementSuccess || '',
        patientCriticality: encData.patientCriticality || '',
        qaDocumentationCriteria: encData.qaDocumentationCriteria?.markdown || '',
        postIncidentReflection: encData.postIncidentReflection?.markdown || '',
        shift: encData.shift?.id || shiftId || '',
        student: encData.student?.id || user?.user?.id || '',
        preceptor: encData.preceptor?.id || '',
        cohort: encData.cohort || '',
        institution: encData.institution || '',
        encounterDate: encData.encounterDate ? new Date(encData.encounterDate).toISOString().slice(0, 16) : '',
        reviewStatus: encData.reviewStatus || 'draft'
      });
      setHasUnsavedChanges(false);
    }
  }, [existingEncounter, shiftId, user?.user?.id]);
  const calculateProgress = useCallback(() => {
    const requiredFields = [
      'encounterDate', 'cohort', 'institution', 'student', 'preceptor', 'shift',
      'patientAge', 'patientBiologicalSex', 'chiefComplaint', 'historyPresentIllness',
      'heartRate', 'systolicBP', 'diastolicBP', 'respiratoryRate', 'oxygenSaturation',
      'patientAlertLevel', 'performedExam', 'performedInterview', 'treatmentInterventions',
      'qaDocumentationCriteria'
    ];
    
    const filledFields = requiredFields.filter(field => {
      const value = formData[field];
      return value !== '' && value !== null && value !== undefined && value !== false;
    });
    
    return Math.round((filledFields.length / requiredFields.length) * 100);
  }, [formData]);

  // Check for duplicate encounter
  const isDuplicateEncounter = useCallback(() => {
    if (!existingShiftEncounters || !user?.user?.id || encounterId) return false;
    return existingShiftEncounters.some(enc => enc.student?.id === user.user.id);
  }, [existingShiftEncounters, user?.user?.id, encounterId]);

  const applyTemplate = useCallback((templateKey) => {
    const template = QUICK_START_TEMPLATES[templateKey];
    if (template) {
      setFormData(prev => ({
        ...prev,
        ...template.data
      }));
      onTemplateModalClose();
      setCurrentTab(0);
    }
  }, [onTemplateModalClose]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    setHasUnsavedChanges(true);
    
    // Trigger auto-save after debounce delay if enabled
    if (autoSaveEnabled && (encounterId || existingEncounter)) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      
      debounceTimer.current = setTimeout(() => {
        if (!isUnmounting.current && hasUnsavedChanges) {
          autoSave();
        }
      }, DEBOUNCE_DELAY);
    }
  }, [autoSaveEnabled, encounterId, existingEncounter, hasUnsavedChanges]);

  const handleSaveAssessment = useCallback((assessmentData) => {
    // Map assessment data to FISDAP fields
    const updates = {};
    
    if (assessmentData.primaryImpression) {
      updates.primaryImpression = assessmentData.primaryImpression;
    }
    if (assessmentData.secondaryImpression) {
      updates.secondaryImpression = assessmentData.secondaryImpression;
    }
    if (assessmentData.fieldImpressions) {
      updates.fieldImpressions = assessmentData.fieldImpressions;
    }
    if (assessmentData.differentialDiagnosis) {
      updates.differentialDiagnosis = assessmentData.differentialDiagnosis;
    }
    if (assessmentData.treatmentInterventions) {
      updates.treatmentInterventions = assessmentData.treatmentInterventions;
    }
    if (assessmentData.patientAlertLevel) {
      updates.patientAlertLevel = assessmentData.patientAlertLevel;
    }
    if (assessmentData.mentalStatus) {
      updates.mentalStatus = assessmentData.mentalStatus;
    }
    
    // Update form data with assessment results
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    
    onAssessmentModalClose();
  }, [onAssessmentModalClose]);

  const handleExportAssessment = useCallback((assessmentData) => {
    // Create a downloadable text file with assessment data
    const exportText = `
Patient Assessment Report
Generated: ${new Date().toLocaleDateString()}

Primary Impression: ${assessmentData.primaryImpression || 'Not specified'}
Secondary Impression: ${assessmentData.secondaryImpression || 'Not specified'}
Field Impressions: ${assessmentData.fieldImpressions || 'Not specified'}
Differential Diagnosis: ${assessmentData.differentialDiagnosis || 'Not specified'}
Treatment & Interventions: ${assessmentData.treatmentInterventions || 'Not specified'}
Patient Alert Level: ${assessmentData.patientAlertLevel || 'Not specified'}
Mental Status: ${assessmentData.mentalStatus || 'Not specified'}

Assessment Notes:
${assessmentData.notes || 'No additional notes'}
    `.trim();

    const blob = new Blob([exportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patient-assessment-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  const quickFillVitals = useCallback((preset) => {
    const presets = {
      normal: {
        heartRate: '72',
        systolicBP: '120',
        diastolicBP: '80',
        respiratoryRate: '16',
        oxygenSaturation: '98',
        temperature: '98.6'
      },
      hypertensive: {
        heartRate: '88',
        systolicBP: '160',
        diastolicBP: '95',
        respiratoryRate: '18',
        oxygenSaturation: '97'
      },
      shock: {
        heartRate: '120',
        systolicBP: '90',
        diastolicBP: '60',
        respiratoryRate: '24',
        oxygenSaturation: '94'
      }
    };
    
    const presetData = presets[preset];
    if (presetData) {
      Object.entries(presetData).forEach(([field, value]) => {
        handleInputChange(field, value);
      });
    }
  }, [handleInputChange]);

  const autoSave = useCallback(async () => {
    if (isUnmounting.current) return;
    
    setAutoSaveStatus('saving');
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
      setAutoSaveStatus('saved');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
      setAutoSaveStatus('error');
    }
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    
    try {
      const submitData = {
        ...formData,
        encounterDate: formData.encounterDate ? new Date(formData.encounterDate).toISOString() : new Date().toISOString(),
        student: { _link: formData.student },
        preceptor: { _link: formData.preceptor },
        shift: { _link: formData.shift },
        chiefComplaint: { markdown: formData.chiefComplaint },
        historyPresentIllness: { markdown: formData.historyPresentIllness },
        eventsPrior: { markdown: formData.eventsPrior },
        lastOralIntake: { markdown: formData.lastOralIntake },
        pastMedicalHistory: { markdown: formData.pastMedicalHistory },
        medications: { markdown: formData.medications },
        allergies: { markdown: formData.allergies },
        onsetTiming: { markdown: formData.onsetTiming },
        provocationPalliation: { markdown: formData.provocationPalliation },
        quality: { markdown: formData.quality },
        radiation: { markdown: formData.radiation },
        severity: { markdown: formData.severity },
        timing: { markdown: formData.timing },
        mentalStatus: { markdown: formData.mentalStatus },
        heentAssessment: { markdown: formData.heentAssessment },
        neckBackAssessment: { markdown: formData.neckBackAssessment },
        cardiacAssessment: { markdown: formData.cardiacAssessment },
        heartSounds: { markdown: formData.heartSounds },
        chestAssessment: { markdown: formData.chestAssessment },
        lungSounds: { markdown: formData.lungSounds },
        abdomenAssessment: { markdown: formData.abdomenAssessment },
        extremitiesAssessment: { markdown: formData.extremitiesAssessment },
        skinAssessment: { markdown: formData.skinAssessment },
        neurologicalAssessment: { markdown: formData.neurologicalAssessment },
        fieldImpressions: { markdown: formData.fieldImpressions },
        differentialDiagnosis: { markdown: formData.differentialDiagnosis },
        treatmentInterventions: { markdown: formData.treatmentInterventions },
        reassessmentResponse: { markdown: formData.reassessmentResponse },
        transportDisposition: { markdown: formData.transportDisposition },
        qaDocumentationCriteria: { markdown: formData.qaDocumentationCriteria },
        postIncidentReflection: formData.postIncidentReflection ? { markdown: formData.postIncidentReflection } : null
      };
      
      if (draftId) {
        await updateEncounter({ id: draftId, ...submitData });
      } else {
        await createEncounter(submitData);
      }
      
      localStorage.removeItem(STORAGE_KEY);
      setSubmitStatus('success');
      
      setTimeout(() => {
        navigate('/instructor.dashboard');
      }, 2000);
    } catch (error) {
      console.error('Submit failed:', error);
      setSubmitStatus('error');
    }
  };
  const saveDraft = async () => {
    try {
      const draftData = {
        ...formData,
        reviewStatus: 'draft',
        encounterDate: formData.encounterDate ? new Date(formData.encounterDate).toISOString() : new Date().toISOString(),
        student: { _link: formData.student },
        preceptor: { _link: formData.preceptor },
        shift: { _link: formData.shift },
        chiefComplaint: { markdown: formData.chiefComplaint },
        historyPresentIllness: { markdown: formData.historyPresentIllness },
        eventsPrior: { markdown: formData.eventsPrior },
        lastOralIntake: { markdown: formData.lastOralIntake },
        pastMedicalHistory: { markdown: formData.pastMedicalHistory },
        medications: { markdown: formData.medications },
        allergies: { markdown: formData.allergies },
        onsetTiming: { markdown: formData.onsetTiming },
        provocationPalliation: { markdown: formData.provocationPalliation },
        quality: { markdown: formData.quality },
        radiation: { markdown: formData.radiation },
        severity: { markdown: formData.severity },
        timing: { markdown: formData.timing },
        mentalStatus: { markdown: formData.mentalStatus },
        heentAssessment: { markdown: formData.heentAssessment },
        neckBackAssessment: { markdown: formData.neckBackAssessment },
        cardiacAssessment: { markdown: formData.cardiacAssessment },
        heartSounds: { markdown: formData.heartSounds },
        chestAssessment: { markdown: formData.chestAssessment },
        lungSounds: { markdown: formData.lungSounds },
        abdomenAssessment: { markdown: formData.abdomenAssessment },
        extremitiesAssessment: { markdown: formData.extremitiesAssessment },
        skinAssessment: { markdown: formData.skinAssessment },
        neurologicalAssessment: { markdown: formData.neurologicalAssessment },
        fieldImpressions: { markdown: formData.fieldImpressions },
        differentialDiagnosis: { markdown: formData.differentialDiagnosis },
        treatmentInterventions: { markdown: formData.treatmentInterventions },
        reassessmentResponse: { markdown: formData.reassessmentResponse },
        transportDisposition: { markdown: formData.transportDisposition },
        qaDocumentationCriteria: { markdown: formData.qaDocumentationCriteria },
        postIncidentReflection: formData.postIncidentReflection ? { markdown: formData.postIncidentReflection } : null
      };
      
      let result;
      if (draftId) {
        result = await updateEncounter({ id: draftId, ...draftData });
      } else {
        result = await createEncounter(draftData);
        setDraftId(result.id);
      }
      
      localStorage.removeItem(STORAGE_KEY);
      setSubmitStatus('draft_saved');
    } catch (error) {
      console.error('Draft save failed:', error);
      setSubmitStatus('error');
    }
  };

  useEffect(() => {
    autoSaveTimer.current = setInterval(() => {
      if (!isUnmounting.current) {
        autoSave();
      }
    }, AUTO_SAVE_INTERVAL);
    
    return () => {
      if (autoSaveTimer.current) {
        clearInterval(autoSaveTimer.current);
      }
    };
  }, [autoSave]);

  useEffect(() => {
    return () => {
      isUnmounting.current = true;
      if (autoSaveTimer.current) {
        clearInterval(autoSaveTimer.current);
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const isLoading = createFetching || updateFetching;

  const isTraumaCase = formData.chiefComplaint.toLowerCase().includes('trauma') || 
                       formData.chiefComplaint.toLowerCase().includes('injury') ||
                       formData.mechanismOfInjury.length > 0;

  const isCardiacCase = formData.chiefComplaint.toLowerCase().includes('chest pain') ||
                        formData.chiefComplaint.toLowerCase().includes('cardiac') ||
                        formData.chiefComplaint.toLowerCase().includes('arrest');

  const progress = calculateProgress();

  return (
    <Box 
      minH="100vh" 
      bg="#111111" 
      color="white" 
      p={{ base: 4, md: 6 }}
    >
      <VStack spacing={{ base: 4, md: 6 }} align="stretch" maxW="1400px" mx="auto">
        <Card bg="#1a1a1a" border="1px solid #333">
          <CardHeader>
            <Flex justify="space-between" align="center">
              <HStack spacing={2}>
                <Button
                  onClick={() => navigate('/')}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  leftIcon={<MdArrowBack />}
                >
                  Back to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/shifts')}
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  leftIcon={<MdArrowBack />}
                >
                  Back to Shifts
                </Button>
              </HStack>
              <Heading size="lg" color="#00ff41">Patient Encounter Form</Heading>
              <HStack spacing={4}>
                <Button
                  onClick={onTemplateModalOpen}
                  size="sm"
                  leftIcon={<MdFlashOn />}
                  colorScheme="blue"
                  variant="outline"
                >
                  Quick Start
                </Button>
                <Badge colorScheme={progress > 75 ? 'green' : progress > 50 ? 'yellow' : 'red'}>
                  {progress}% Complete
                </Badge>
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* Progress Bar */}
              <Box>
                <Progress 
                  value={progress} 
                  colorScheme={progress > 75 ? 'green' : progress > 50 ? 'yellow' : 'red'}
                  size="lg"
                  borderRadius="full"
                />
                <Text fontSize="sm" color="gray.400" mt={2}>
                  {progress}% of required fields completed
                </Text>
              </Box>

              {/* Auto-save Status */}
              {autoSaveStatus !== 'idle' && (
                <Alert status={autoSaveStatus === 'error' ? 'error' : 'success'} size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">
                    {autoSaveStatus === 'saving' && 'Saving draft...'}
                    {autoSaveStatus === 'saved' && `Draft saved ${lastSaved ? 'at ' + lastSaved.toLocaleTimeString() : ''}`}
                    {autoSaveStatus === 'error' && 'Failed to save draft'}
                  </Text>
                </Alert>
              )}

              {/* Submit Status */}
              {submitStatus && (
                <Alert
                  status={
                    submitStatus === 'success' || submitStatus === 'draft_saved' ? 'success' : 
                    submitStatus === 'error' ? 'error' : 'info'
                  }
                >
                  <AlertIcon />
                  <Box>
                    <AlertTitle>
                      {submitStatus === 'submitting' && 'Submitting...'}
                      {submitStatus === 'success' && 'Success!'}
                      {submitStatus === 'draft_saved' && 'Draft Saved!'}
                      {submitStatus === 'error' && 'Error!'}
                    </AlertTitle>
                    <AlertDescription>
                      {submitStatus === 'submitting' && 'Please wait while your encounter is being submitted.'}
                      {submitStatus === 'success' && 'Your encounter has been submitted successfully. Redirecting...'}
                      {submitStatus === 'draft_saved' && 'Your draft has been saved successfully.'}
                      {submitStatus === 'error' && 'There was an error submitting your encounter. Please try again.'}
                    </AlertDescription>
                  </Box>
                </Alert>
              )}

              {/* Errors */}
              {(createError || updateError) && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Error!</AlertTitle>
                    <AlertDescription>
                      {createError?.message || updateError?.message}
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Form Content */}
        <Box as="form" onSubmit={handleSubmit}>
          <VStack spacing={8} align="stretch">
            {/* Encounter Details */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Encounter Details</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Encounter Date</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.encounterDate}
                    onChange={(e) => handleInputChange('encounterDate', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Cohort</FormLabel>
                  <Input
                    value={formData.cohort}
                    onChange={(e) => handleInputChange('cohort', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Institution</FormLabel>
                  <Input
                    value={formData.institution}
                    onChange={(e) => handleInputChange('institution', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>
            {/* Personnel */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Personnel</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Student ID</FormLabel>
                  <Input
                    value={formData.student}
                    onChange={(e) => handleInputChange('student', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Preceptor ID</FormLabel>
                  <Input
                    value={formData.preceptor}
                    onChange={(e) => handleInputChange('preceptor', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Shift ID</FormLabel>
                  <Input
                    value={formData.shift}
                    onChange={(e) => handleInputChange('shift', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Patient Demographics */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Patient Demographics</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Patient Age (Years)</FormLabel>
                  <NumberInput
                    value={formData.patientAge}
                    onChange={(valueString) => handleInputChange('patientAge', valueString)}
                    min={0}
                    max={120}
                  >
                    <NumberInputField
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                    />
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Age (Months if &lt; 2 years)</FormLabel>
                  <NumberInput
                    value={formData.patientAgeMonths}
                    onChange={(valueString) => handleInputChange('patientAgeMonths', valueString)}
                    min={0}
                    max={11}
                  >
                    <NumberInputField
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                    />
                  </NumberInput>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Gender</FormLabel>
                  <Select
                    value={formData.patientGender}
                    onChange={(e) => handleInputChange('patientGender', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  >
                    <option value="">Select gender</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Ethnicity</FormLabel>
                  <Select
                    value={formData.patientEthnicity}
                    onChange={(e) => handleInputChange('patientEthnicity', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  >
                    <option value="">Select ethnicity</option>
                    <option value="african_american">African American</option>
                    <option value="asian">Asian</option>
                    <option value="caucasian">Caucasian</option>
                    <option value="hispanic">Hispanic</option>
                    <option value="native_american">Native American</option>
                    <option value="other">Other</option>
                    <option value="not_specified">Not Specified</option>
                  </Select>
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Chief Complaint & History */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Chief Complaint & History</Heading>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel color="gray.300">Chief Complaint</FormLabel>
                  <Textarea
                    value={formData.chiefComplaint}
                    onChange={(e) => handleInputChange('chiefComplaint', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="gray.300">History of Present Illness</FormLabel>
                    <Textarea
                      value={formData.historyPresentIllness}
                      onChange={(e) => handleInputChange('historyPresentIllness', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={4}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.300">Events Prior to Arrival</FormLabel>
                    <Textarea
                      value={formData.eventsPrior}
                      onChange={(e) => handleInputChange('eventsPrior', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={4}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.300">Last Oral Intake</FormLabel>
                    <Textarea
                      value={formData.lastOralIntake}
                      onChange={(e) => handleInputChange('lastOralIntake', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.300">Past Medical History</FormLabel>
                    <Textarea
                      value={formData.pastMedicalHistory}
                      onChange={(e) => handleInputChange('pastMedicalHistory', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.300">Medications</FormLabel>
                    <Textarea
                      value={formData.medications}
                      onChange={(e) => handleInputChange('medications', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.300">Allergies</FormLabel>
                    <Textarea
                      value={formData.allergies}
                      onChange={(e) => handleInputChange('allergies', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </Box>

            {/* OPQRST Assessment */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">OPQRST Assessment</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl>
                  <FormLabel color="gray.300">Onset & Timing</FormLabel>
                  <Textarea
                    value={formData.onsetTiming}
                    onChange={(e) => handleInputChange('onsetTiming', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Provocation & Palliation</FormLabel>
                  <Textarea
                    value={formData.provocationPalliation}
                    onChange={(e) => handleInputChange('provocationPalliation', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Quality</FormLabel>
                  <Textarea
                    value={formData.quality}
                    onChange={(e) => handleInputChange('quality', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Radiation</FormLabel>
                  <Textarea
                    value={formData.radiation}
                    onChange={(e) => handleInputChange('radiation', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Severity (1-10)</FormLabel>
                  <Textarea
                    value={formData.severity}
                    onChange={(e) => handleInputChange('severity', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Timing</FormLabel>
                  <Textarea
                    value={formData.timing}
                    onChange={(e) => handleInputChange('timing', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>
              {/* Vital Signs */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Vital Signs</Heading>
              
              {/* Quick Fill Buttons */}
              <HStack spacing={2} mb={4}>
                <Text fontSize="sm" color="gray.400">Quick Fill:</Text>
                <ButtonGroup size="xs" variant="outline">
                  <Button onClick={() => quickFillVitals('normal')} colorScheme="green">
                    Normal
                  </Button>
                  <Button onClick={() => quickFillVitals('hypertensive')} colorScheme="orange">
                    Hypertensive
                  </Button>
                  <Button onClick={() => quickFillVitals('shock')} colorScheme="red">
                    Shock
                  </Button>
                </ButtonGroup>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Heart Rate (bpm)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.heartRate}
                      onChange={(valueString) => handleInputChange('heartRate', valueString)}
                      min={20}
                      max={250}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.heartRateUta}
                      onChange={(e) => handleInputChange('heartRateUta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel color="gray.300">Systolic BP (mmHg)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.systolicBP}
                      onChange={(valueString) => handleInputChange('systolicBP', valueString)}
                      min={20}
                      max={300}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.systolicBPUta}
                      onChange={(e) => handleInputChange('systolicBPUta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel color="gray.300">Diastolic BP (mmHg)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.diastolicBP}
                      onChange={(valueString) => handleInputChange('diastolicBP', valueString)}
                      min={10}
                      max={200}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.diastolicBPUta}
                      onChange={(e) => handleInputChange('diastolicBPUta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel color="gray.300">Respiratory Rate (breaths/min)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.respiratoryRate}
                      onChange={(valueString) => handleInputChange('respiratoryRate', valueString)}
                      min={5}
                      max={60}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.respiratoryRateUta}
                      onChange={(e) => handleInputChange('respiratoryRateUta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel color="gray.300">Oxygen Saturation (%)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.oxygenSaturation}
                      onChange={(valueString) => handleInputChange('oxygenSaturation', valueString)}
                      min={0}
                      max={100}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.oxygenSaturationUta}
                      onChange={(e) => handleInputChange('oxygenSaturationUta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
                
                <FormControl>
                  <FormLabel color="gray.300">Temperature (°F)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.temperature}
                      onChange={(valueString) => handleInputChange('temperature', valueString)}
                      min={95}
                      max={108}
                      step={0.1}
                      precision={1}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.temperatureUta}
                      onChange={(e) => handleInputChange('temperatureUta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
                
                <FormControl>
                  <FormLabel color="gray.300">Blood Glucose Level (mg/dL)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.bgl}
                      onChange={(valueString) => handleInputChange('bgl', valueString)}
                      min={20}
                      max={600}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.bglUta}
                      onChange={(e) => handleInputChange('bglUta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
                
                <FormControl>
                  <FormLabel color="gray.300">End-Tidal CO2 (mmHg)</FormLabel>
                  <HStack>
                    <NumberInput
                      value={formData.etco2}
                      onChange={(valueString) => handleInputChange('etco2', valueString)}
                      min={10}
                      max={100}
                      flex={1}
                    >
                      <NumberInputField
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      />
                    </NumberInput>
                    <Checkbox
                      isChecked={formData.etco2Uta}
                      onChange={(e) => handleInputChange('etco2Uta', e.target.checked)}
                      colorScheme="red"
                      size="sm"
                    >
                      UTA
                    </Checkbox>
                  </HStack>
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Neurological Assessment */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Neurological Assessment</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Patient Alert Level</FormLabel>
                  <Select
                    value={formData.patientAlertLevel}
                    onChange={(e) => handleInputChange('patientAlertLevel', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  >
                    <option value="">Select alert level</option>
                    <option value="alert">Alert</option>
                    <option value="voice_responsive">Voice Responsive</option>
                    <option value="pain_responsive">Pain Responsive</option>
                    <option value="unresponsive">Unresponsive</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Mental Status</FormLabel>
                  <Textarea
                    value={formData.mentalStatus}
                    onChange={(e) => handleInputChange('mentalStatus', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>
              {/* Physical Examination */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Physical Examination</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel color="gray.300">HEENT Assessment</FormLabel>
                  <Textarea
                    value={formData.heentAssessment}
                    onChange={(e) => handleInputChange('heentAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Neck & Back Assessment</FormLabel>
                  <Textarea
                    value={formData.neckBackAssessment}
                    onChange={(e) => handleInputChange('neckBackAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Cardiac Assessment</FormLabel>
                  <Textarea
                    value={formData.cardiacAssessment}
                    onChange={(e) => handleInputChange('cardiacAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Heart Sounds</FormLabel>
                  <Textarea
                    value={formData.heartSounds}
                    onChange={(e) => handleInputChange('heartSounds', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Chest Assessment</FormLabel>
                  <Textarea
                    value={formData.chestAssessment}
                    onChange={(e) => handleInputChange('chestAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Lung Sounds</FormLabel>
                  <Textarea
                    value={formData.lungSounds}
                    onChange={(e) => handleInputChange('lungSounds', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Abdomen Assessment</FormLabel>
                  <Textarea
                    value={formData.abdomenAssessment}
                    onChange={(e) => handleInputChange('abdomenAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Extremities Assessment</FormLabel>
                  <Textarea
                    value={formData.extremitiesAssessment}
                    onChange={(e) => handleInputChange('extremitiesAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Skin Assessment</FormLabel>
                  <Textarea
                    value={formData.skinAssessment}
                    onChange={(e) => handleInputChange('skinAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Neurological Assessment</FormLabel>
                  <Textarea
                    value={formData.neurologicalAssessment}
                    onChange={(e) => handleInputChange('neurologicalAssessment', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Clinical Assessment */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md" color="#00ff41">Clinical Assessment</Heading>
                <Button
                  onClick={onAssessmentModalOpen}
                  leftIcon={<MdAssessment />}
                  colorScheme="blue"
                  variant="solid"
                  size="md"
                >
                  Open Interactive Assessment
                </Button>
              </Flex>
              <VStack spacing={4} align="stretch">
                <HStack spacing={6}>
                  <Checkbox
                    isChecked={formData.performedExam}
                    onChange={(e) => handleInputChange('performedExam', e.target.checked)}
                    colorScheme="green"
                  >
                    Performed Physical Exam
                  </Checkbox>
                  <Checkbox
                    isChecked={formData.performedInterview}
                    onChange={(e) => handleInputChange('performedInterview', e.target.checked)}
                    colorScheme="green"
                  >
                    Performed Patient Interview
                  </Checkbox>
                </HStack>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="gray.300">Primary Impression</FormLabel>
                    <Textarea
                      value={formData.primaryImpression}
                      onChange={(e) => handleInputChange('primaryImpression', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.300">Secondary Impression</FormLabel>
                    <Textarea
                      value={formData.secondaryImpression}
                      onChange={(e) => handleInputChange('secondaryImpression', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel color="gray.300">Field Impressions</FormLabel>
                    <Textarea
                      value={formData.fieldImpressions}
                      onChange={(e) => handleInputChange('fieldImpressions', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel color="gray.300">Differential Diagnosis</FormLabel>
                    <Textarea
                      value={formData.differentialDiagnosis}
                      onChange={(e) => handleInputChange('differentialDiagnosis', e.target.value)}
                      bg="#222"
                      border="1px solid #444"
                      _focus={{ borderColor: "#00ff41" }}
                      rows={3}
                    />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </Box>

            {/* Interventions & Transport */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Interventions & Transport</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Treatment & Interventions</FormLabel>
                  <Textarea
                    value={formData.treatmentInterventions}
                    onChange={(e) => handleInputChange('treatmentInterventions', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={4}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Reassessment & Patient Response</FormLabel>
                  <Textarea
                    value={formData.reassessmentResponse}
                    onChange={(e) => handleInputChange('reassessmentResponse', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={4}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color="gray.300">Transport Disposition</FormLabel>
                  <Textarea
                    value={formData.transportDisposition}
                    onChange={(e) => handleInputChange('transportDisposition', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={4}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>
              {/* Trauma/Cardiac Specific */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Trauma/Cardiac Specific</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel color="gray.300">Mechanism of Injury</FormLabel>
                  <Textarea
                    value={formData.mechanismOfInjury.join(', ')}
                    onChange={(e) => handleInputChange('mechanismOfInjury', e.target.value.split(', ').filter(Boolean))}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={3}
                    placeholder="Separate multiple mechanisms with commas"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Cause of Injury</FormLabel>
                  <Select
                    value={formData.causeOfInjury}
                    onChange={(e) => handleInputChange('causeOfInjury', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  >
                    <option value="">Select cause</option>
                    <option value="aircraft_related">Aircraft Related</option>
                    <option value="bicycle_accident">Bicycle Accident</option>
                    <option value="bites">Bites</option>
                    <option value="chemical_poisoning">Chemical Poisoning</option>
                    <option value="drowning">Drowning</option>
                    <option value="drug_poisoning">Drug Poisoning</option>
                    <option value="electrocution">Electrocution</option>
                    <option value="excessive_cold">Excessive Cold</option>
                    <option value="excessive_heat">Excessive Heat</option>
                    <option value="fall">Fall</option>
                    <option value="family_violence">Family Violence</option>
                    <option value="fire_flames">Fire/Flames</option>
                    <option value="firearm">Firearm</option>
                    <option value="lightning">Lightning</option>
                    <option value="machinery_accident">Machinery Accident</option>
                    <option value="mechanical_suffocation">Mechanical Suffocation</option>
                    <option value="motor_vehicle_accident">Motor Vehicle Accident</option>
                    <option value="radiation_exposure">Radiation Exposure</option>
                    <option value="rape">Rape</option>
                    <option value="smoke_inhalation">Smoke Inhalation</option>
                    <option value="stabbing_cutting">Stabbing/Cutting</option>
                    <option value="venomous_stings">Venomous Stings</option>
                    <option value="water_transport_accident">Water Transport Accident</option>
                    <option value="na">N/A</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Intent of Injury</FormLabel>
                  <Select
                    value={formData.intentOfInjury}
                    onChange={(e) => handleInputChange('intentOfInjury', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  >
                    <option value="">Select intent</option>
                    <option value="na">N/A</option>
                    <option value="unintentional">Unintentional</option>
                    <option value="self_inflicted">Self Inflicted</option>
                    <option value="assault">Assault</option>
                    <option value="unknown">Unknown</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Arrest Witnessed By</FormLabel>
                  <Select
                    value={formData.arrestWitnessedBy}
                    onChange={(e) => handleInputChange('arrestWitnessedBy', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  >
                    <option value="">Select witness</option>
                    <option value="no_arrest">No Arrest</option>
                    <option value="unknown">Unknown</option>
                    <option value="lay_person">Lay Person</option>
                    <option value="not_witnessed">Not Witnessed</option>
                    <option value="healthcare_provider">Healthcare Provider</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Return of Pulse</FormLabel>
                  <Select
                    value={formData.returnOfPulse}
                    onChange={(e) => handleInputChange('returnOfPulse', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                  >
                    <option value="">Select status</option>
                    <option value="unknown">Unknown</option>
                    <option value="no_resuscitation">No Resuscitation</option>
                    <option value="on_hospital_arrival">On Hospital Arrival</option>
                    <option value="brief_at_any_time">Brief at Any Time</option>
                    <option value="no_return">No Return</option>
                  </Select>
                </FormControl>
                <VStack align="stretch">
                  <Checkbox
                    isChecked={formData.airwayManagementRequired}
                    onChange={(e) => handleInputChange('airwayManagementRequired', e.target.checked)}
                    colorScheme="green"
                  >
                    Airway Management Required
                  </Checkbox>
                  {formData.airwayManagementRequired && (
                    <FormControl>
                      <FormLabel color="gray.300">Airway Management Success</FormLabel>
                      <Select
                        value={formData.airwayManagementSuccess}
                        onChange={(e) => handleInputChange('airwayManagementSuccess', e.target.value)}
                        bg="#222"
                        border="1px solid #444"
                        _focus={{ borderColor: "#00ff41" }}
                      >
                        <option value="">Select success</option>
                        <option value="not_performed">Not Performed</option>
                        <option value="successful">Successful</option>
                        <option value="unsuccessful">Unsuccessful</option>
                      </Select>
                    </FormControl>
                  )}
                </VStack>
              </SimpleGrid>
            </Box>

            {/* Patient Classification */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Patient Classification</Heading>
              <FormControl isRequired>
                <FormLabel color="gray.300">Patient Criticality</FormLabel>
                <Select
                  value={formData.patientCriticality}
                  onChange={(e) => handleInputChange('patientCriticality', e.target.value)}
                  bg="#222"
                  border="1px solid #444"
                  _focus={{ borderColor: "#00ff41" }}
                >
                  <option value="">Select criticality</option>
                  <option value="green_ambulatory">Green (Ambulatory)</option>
                  <option value="yellow_not_life_threatening">Yellow (Not Life Threatening)</option>
                  <option value="red_critical">Red (Critical)</option>
                  <option value="black_dead_on_arrival">Black (Dead on Arrival)</option>
                </Select>
              </FormControl>
            </Box>

            {/* Documentation */}
            <Box p={6} bg="#1a1a1a" borderRadius="md" border="1px solid #333">
              <Heading size="md" mb={4} color="#00ff41">Documentation</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.300">QA Documentation Criteria</FormLabel>
                  <Textarea
                    value={formData.qaDocumentationCriteria}
                    onChange={(e) => handleInputChange('qaDocumentationCriteria', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={4}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.300">Post-Incident Reflection</FormLabel>
                  <Textarea
                    value={formData.postIncidentReflection}
                    onChange={(e) => handleInputChange('postIncidentReflection', e.target.value)}
                    bg="#222"
                    border="1px solid #444"
                    _focus={{ borderColor: "#00ff41" }}
                    rows={4}
                  />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Form Actions */}
            <HStack justify="center" spacing={4} pt={6}>
              <Button
                type="button"
                onClick={saveDraft}
                size="lg"
                variant="outline"
                colorScheme="blue"
                isLoading={isLoading}
                loadingText="Saving..."
              >
                Save Draft
              </Button>
              <Button
                type="submit"
                size="lg"
                colorScheme="green"
                isLoading={isLoading}
                loadingText="Submitting..."
              >
                Submit Encounter
              </Button>
            </HStack>
          </VStack>
        </Box>

        {/* Quick Start Template Modal */}
        <Modal isOpen={isTemplateModalOpen} onClose={onTemplateModalClose} size="lg">
          <ModalOverlay />
          <ModalContent bg="#1a1a1a" border="1px solid #333">
            <ModalHeader color="#00ff41">Quick Start Templates</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                {Object.entries(QUICK_START_TEMPLATES).map(([key, template]) => (
                  <Button
                    key={key}
                    onClick={() => applyTemplate(key)}
                    size="lg"
                    variant="outline"
                    colorScheme="blue"
                    leftIcon={<Text fontSize="xl">{template.icon}</Text>}
                    height="auto"
                    py={6}
                    whiteSpace="normal"
                    textAlign="center"
                  >
                    {template.name}
                  </Button>
                ))}
              </SimpleGrid>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onTemplateModalClose}>Cancel</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Interactive Patient Assessment Modal */}
        <Modal isOpen={isAssessmentModalOpen} onClose={onAssessmentModalClose} size="full">
          <ModalOverlay />
          <ModalContent bg="#111111" maxW="95vw" maxH="95vh">
            <ModalHeader color="#00ff41">Interactive Patient Assessment</ModalHeader>
            <ModalCloseButton />
            <ModalBody p={0}>
              <InteractivePatientAssessment
                initialData={{
                  primaryImpression: formData.primaryImpression,
                  secondaryImpression: formData.secondaryImpression,
                  fieldImpressions: formData.fieldImpressions,
                  differentialDiagnosis: formData.differentialDiagnosis,
                  treatmentInterventions: formData.treatmentInterventions,
                  patientAlertLevel: formData.patientAlertLevel,
                  mentalStatus: formData.mentalStatus,
                  chiefComplaint: formData.chiefComplaint,
                  patientAge: formData.patientAge,
                  patientGender: formData.patientGender,
                  heartRate: formData.heartRate,
                  systolicBP: formData.systolicBP,
                  diastolicBP: formData.diastolicBP,
                  respiratoryRate: formData.respiratoryRate,
                  oxygenSaturation: formData.oxygenSaturation
                }}
                onSave={handleSaveAssessment}
                onExport={handleExportAssessment}
                onClose={onAssessmentModalClose}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
}